import { useQuery } from "react-query"
import { AxiosError } from "axios"
import { IHttpErrorResponseModel } from "@common-models/*"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR } from "@common-constants/*"
import { toast } from "react-toastify"
import React, { createContext, useContext, useEffect, useRef, useState } from "react"
import { consultantsSuivisListingRequest } from "../core/_requests"
import { IIsBulkSelectionEnabledPropsModel } from "../../../../../models"
import { IConsultantsSuivisListingContextPropsModel, IConsultantsSuivisListingRequestModel, IConsultantsSuivisListingResponseModel } from "./core/_models"
import ConsultantsSuivisListingSearchBar from "./_ConsultantsSuivisListingSearchBar"
import ConsultantsSuivisListingFilters from "./_ConsultantsSuivisListingFilters"
import ConsultantsSuivisListingList from "./_ConsultantsSuivisListingList"
import { useLocation } from "react-router-dom"

const REACT_QUERY_KEY_GET_LISTING_CONSULTANTS = "REACT_QUERY_KEY_GET_LISTING_CONSULTANTS"
const ConsultantsSuivisListingContext = createContext<IConsultantsSuivisListingContextPropsModel>({} as IConsultantsSuivisListingContextPropsModel)
export const useConsultantsSuivisListingContext = () => useContext(ConsultantsSuivisListingContext)
const ConsultantsSuivisListing = ({ consultants, pageLength = 20, isSearchBarVisible = true, isTitleVisible = true, isPaginationVisible = true }: IPropsModel) => {
     // Page states & URL parameters
     const location = useLocation() as {
          state: {
               consultants?: number[]
          }
     }
     const urlParams = new URLSearchParams(window.location.search)

     const urlParamsConsultants = urlParams.get("consultants") as number[] | null

     // Filters, item in preview & bulk selection
     const [filters, setFilters] = useState<IConsultantsSuivisListingRequestModel>({
          page: 1,
          pageLength,
          consultants: consultants || location.state?.consultants || urlParamsConsultants || undefined,
     })

     // Referencing filters form, simple search bar form & preview container
     const filtersFormikRef = useRef<any>(null)
     const simpleSearchBarFormikRef = useRef<any>(null)

     const [areFiltersVisible, setAreFiltersVisible] = useState<boolean>(false)
     const [isBulkSelectionEnabled, setIsBulkSelectionEnabled] = useState<IIsBulkSelectionEnabledPropsModel>({
          enabled: false,
          checkedElementsInPage: [],
          areAllElementsInAllPagesChecked: false,
     })

     // Main listing query
     const listingQuery = useQuery<IConsultantsSuivisListingResponseModel, AxiosError>(REACT_QUERY_KEY_GET_LISTING_CONSULTANTS, async () => {
          return consultantsSuivisListingRequest(filters)
               .then(r => {
                    // Stop loaders (filters + simple search bar)
                    filtersFormikRef.current.setSubmitting(false)
                    simpleSearchBarFormikRef.current.setSubmitting(false)

                    return r.data
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data

                    // Set form errors in the filters form
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) filtersFormikRef.current.setFieldError(key, error.errors[key])
                    }

                    // Stop loaders (filters + simple search bar)
                    filtersFormikRef.current.setSubmitting(false)
                    simpleSearchBarFormikRef.current.setSubmitting(false)

                    // Toast error
                    toast.error(error?.detail, { autoClose: false })

                    throw e
               })
     })

     useEffect(() => {
          listingQuery.refetch().then()
     }, [filters])

     return (
          <>
               <ConsultantsSuivisListingContext.Provider
                    value={{
                         areFiltersVisible,
                         setAreFiltersVisible,
                         filters,
                         setFilters,
                         REACT_QUERY_KEY_GET_LISTING_CONSULTANTS,
                         listingQuery,
                         filtersFormikRef,
                         simpleSearchBarFormikRef,
                         isBulkSelectionEnabled,
                         setIsBulkSelectionEnabled,
                         isSearchBarVisible,
                         isTitleVisible,
                         isPaginationVisible,
                    }}
               >
                    {/* Adapt view to display */}
                    {window.innerWidth >= 1200 ? <ViewXlOrHigher /> : <ViewLgOrLess />}
               </ConsultantsSuivisListingContext.Provider>
          </>
     )
}

// View display Lg or less
const ViewLgOrLess = () => {
     const context = useConsultantsSuivisListingContext()

     return (
          <>
               <div className={`${context.areFiltersVisible ? "d-none" : context.isSearchBarVisible ? "mb-2" : ""}`}>
                    <ConsultantsSuivisListingSearchBar />
               </div>

               <div className={`${!context.areFiltersVisible ? "d-none" : context.isSearchBarVisible ? "mb-2" : ""}`}>
                    <ConsultantsSuivisListingFilters />
               </div>

               <ConsultantsSuivisListingList />
          </>
     )
}

// View display Xl or higher
const ViewXlOrHigher = () => {
     const context = useConsultantsSuivisListingContext()
     return (
          <>
               <div className={"row"}>
                    {/* Filters become visible when the filter icon is clicked */}
                    <div className={`col-5 ${!context.areFiltersVisible ? "d-none" : ""}`}>
                         <ConsultantsSuivisListingFilters />
                    </div>
                    <div className={`${!context.areFiltersVisible ? "col-12" : "col-7"}`}>
                         {/* Simple bar search will be shown only if filters are not displayed */}
                         <div className={`${context.isSearchBarVisible && "mb-2"} w-50 ${context.areFiltersVisible ? "d-none" : ""}`}>
                              <ConsultantsSuivisListingSearchBar />
                         </div>

                         <ConsultantsSuivisListingList />
                    </div>
               </div>
          </>
     )
}

interface IPropsModel {
     consultants?: number[]
     pageLength?: number
     isSearchBarVisible?: boolean
     isPaginationVisible?: boolean
     isTitleVisible?: boolean
}

export default ConsultantsSuivisListing
